(() => {
  if (customElements.get('gameday-partner-inquiry')) return;
  let formSequence = 0;
  class GamedayPartnerInquiry extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.openButton = this.querySelector('[data-gpi-open]');
      this.dialog = this.querySelector('[data-gpi-dialog]');
      this.message = this.querySelector('[data-gpi-message]');
      this.verifyButton = this.querySelector('[data-gpi-verify]');
      this.context = this.querySelector('[data-gpi-context]');
      this.openButton?.addEventListener('click', () => this.open());
      this.querySelector('[data-gpi-close]')?.addEventListener('click', () => this.close());
      this.verifyButton?.addEventListener('click', () => this.verify());
      this.dialog?.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.close();
      });
      this.dialog?.addEventListener('close', () => {
        document.documentElement.style.overflow = this.previousOverflow ?? '';
        this.openButton?.focus({ preventScroll: true });
      });
    }
    status(text, state = 'info') {
      this.message.textContent = text;
      this.message.dataset.state = state;
      this.message.hidden = !text;
    }
    prepareForm() {
      if (this.form) return;
      const source = this.querySelector('[data-gpi-form-template]');
      this.querySelector('[data-gpi-form-host]').append(source.content.cloneNode(true));
      this.form = this.querySelector('[data-gpi-form-host] form');
      this.form.id += '-' + (++formSequence);
      this.sendButton = this.form.querySelector('[data-gpi-submit]');
      // Install only this form's transport before Shopify binds its CAPTCHA.
      // Shopify wraps this method and invokes it with the generated token present.
      this.form.submit = () => this.send();
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!this.captchaReady || this.submitting || this.sent || !this.form.reportValidity()) return;
        this.submitting = true;
        this.sendButton.disabled = true;
        this.status('Verifying and sending your request…');
        this.submitWatch = window.setTimeout(() => {
          if (!this.sending && !this.sent) {
            this.submitting = false; this.sendButton.disabled = false;
            this.status('Verification did not finish. Your message is still here; please try again.', 'error');
          }
        }, 45000);
        this.form.submit();
      }, true);
      this.protectForm();
    }
    protectForm() {
      if (this.captchaReady || this.protecting) return;
      this.protecting = true;
      this.sendButton.disabled = true;
      const protect = window.Shopify?.captcha?.protect;
      if (typeof protect !== 'function') {
        this.protecting = false;
        this.status('The form is still loading. Close and reopen this inquiry to try again.', 'error');
        return;
      }
      this.protectWatch = window.setTimeout(() => {
        if (!this.captchaReady) {
          this.protecting = false;
          this.status('Verification could not load. Your message is preserved; close and reopen to retry.', 'error');
        }
      }, 20000);
      protect(this.form, () => {
        window.clearTimeout(this.protectWatch);
        this.protecting = false;
        this.captchaReady = true;
        this.sendButton.disabled = false;
        this.status('');
      });
    }
    setProductContext() {
      const url = new URL(this.dataset.productUrl, window.location.origin);
      if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) return false;
      url.searchParams.set('view', 'gameday-partner-inquiry');
      const selected = this.closest('gameday-authentic-kit-configurator')?.selected;
      const nameField = this.form.querySelector('[data-gpi-design-name]');
      const urlField = this.form.querySelector('[data-gpi-design-url]');
      const detail = this.form.closest('.gpi-form').querySelector('[data-gpi-design-context]');
      nameField.disabled = true; urlField.disabled = true; detail.hidden = true;
      this.context.textContent = 'Keep this proof in view while you contact Gameday Flags.';
      if (/^authentic-gameday-flag-[a-z0-9-]+$/.test(selected?.handle || '')) {
        const name = String(selected.name || selected.title || selected.handle).slice(0,200);
        nameField.value = name; nameField.disabled = false;
        urlField.value = new URL('/products/' + selected.handle, window.location.origin).href; urlField.disabled = false;
        detail.textContent = 'Selected flag: ' + name; detail.hidden = false;
        this.context.textContent = detail.textContent;
      }
      this.form.querySelector('input[name="return_to"]').value = url.pathname + url.search;
      return true;
    }
    open() {
      if (!this.dialog || this.dialog.open) return;
      this.prepareForm();
      if (!this.setProductContext()) return;
      if (!this.sent) this.protectForm();
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.dialog.showModal();
    }
    close() { if (this.dialog?.open) this.dialog.close(); }
    finish() {
      this.sent = true;
      this.submitting = false;
      this.form.hidden = true;
      this.verifyButton.hidden = true;
      this.status('Thank you. Your product inquiry has been submitted. You can close this window to return to the proof.', 'success');
    }
    async send() {
      if (this.sending || this.sent) return;
      this.sending = true;
      window.clearTimeout(this.submitWatch);
      try {
        const action = new URL(this.form.action, window.location.origin);
        if (action.origin !== window.location.origin || !action.pathname.endsWith('/contact')) throw new Error('The contact route is unavailable. Your message is preserved.');
        const response = await fetch(action.href, {method:'POST', body:new FormData(this.form), credentials:'same-origin', headers:{Accept:'text/html'}});
        const markup = await response.text();
        const result = new DOMParser().parseFromString(markup,'text/html');
        const resultUrl = new URL(response.url, window.location.origin);
        if (response.ok && result.querySelector('[data-gpi-native-success]')) { this.finish(); return; }
        if (resultUrl.origin === window.location.origin && resultUrl.pathname === '/challenge') {
          this.challengeUrl = resultUrl.href;
          this.verifyButton.hidden = false;
          this.status('Shopify requires an additional verification step. Complete it in the verification window; this product and your inquiry will stay here.', 'error');
          return;
        }
        const error = result.querySelector('[data-gpi-native-errors]');
        throw new Error(error?.textContent.trim() || (response.status === 429 ? 'Too many attempts. Please wait before trying again; your message is preserved.' : 'Delivery was not confirmed. Your message is preserved; please try again later.'));
      } catch (error) {
        this.status(error?.message || 'Delivery was not confirmed. Your message is preserved.', 'error');
      } finally {
        this.sending = false; this.submitting = false;
        if (!this.sent) this.sendButton.disabled = false;
      }
    }
    verify() {
      if (!this.challengeUrl) return;
      const url = new URL(this.challengeUrl);
      if (url.origin !== window.location.origin || url.pathname !== '/challenge') return;
      const popup = window.open(url.href, 'gameday-partner-verification', 'popup=yes,width=640,height=760');
      if (!popup) { this.status('Allow the Shopify verification window to complete this request. Your message stays here.', 'error'); return; }
      window.clearInterval(this.verifyWatch);
      const until = Date.now() + 180000;
      this.verifyWatch = window.setInterval(() => {
        if (popup.closed || Date.now() > until) { window.clearInterval(this.verifyWatch); return; }
        try {
          if (popup.location.origin === window.location.origin && popup.document.querySelector('[data-gpi-native-success]')) {
            this.finish(); window.clearInterval(this.verifyWatch); popup.close();
          }
        } catch (_) { /* Wait for Shopify's verification to return to this store. */ }
      }, 700);
    }
  }
  customElements.define('gameday-partner-inquiry', GamedayPartnerInquiry);
})();
